import React from 'react';
import { CheckCircle, Globe, Award, Users, Target } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Product Variety',
      description: 'Wide range of African food products sourced from Nigerian suppliers and producers'
    },
    {
      icon: Globe,
      title: 'International Focus',
      description: 'Building trade relationships between Nigerian suppliers and global buyers'
    },
    {
      icon: Users,
      title: 'Partnership Approach',
      description: 'Long-term relationships with both suppliers and international buyers'
    },
    {
      icon: Target,
      title: 'Buyer Support',
      description: 'Flexible approach to wholesale enquiries and export coordination needs'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxwYXJ0bmVyc2hpcHxlbnwwfHx8fDE3NzEyODAxNzh8MA&ixlib=rb-4.1.0&q=85"
            alt="About Folalabi Farms"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#1B5E20]/80"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">About Folalabi Farms</h1>
          <p className="text-xl text-white/95 max-w-3xl mx-auto">
            Bridging African agricultural excellence with global markets
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#333333] mb-6 text-center">Who We Are</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                <strong>Folalabi Farms</strong> is an African food sourcing and export business focused on connecting Nigerian agricultural and food products with international markets. We work with wholesalers, distributors, African grocery retailers, and food businesses that require consistent access to African food products.
              </p>
              <p>
                Our business model centers on building reliable supply channels between trusted Nigerian producers and international buyers. We support sourcing discussions, export coordination, and wholesale supply enquiries for a broad range of African food products including beans, garri, palm oil, yam, egusi, dried fish, plantain flour, and many others.
              </p>
              <p>
                Operating with a presence in both Nigeria and the United Kingdom, we aim to support international buyers seeking to expand their African product offering while strengthening trade relationships between Nigeria and global markets.
              </p>
              <p>
                Folalabi Farms is building toward becoming a trusted partner for African food supply, recognized for reliable sourcing capability, supplier relationships, and commitment to buyer satisfaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-full flex items-center justify-center mb-4">
                  <Target className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#333333] mb-4">Our Mission</h3>
                <p className="text-gray-700">
                  To connect Nigerian agricultural and food products with global markets by building reliable supply channels, supporting wholesale enquiries, and fostering long-term trade relationships between Nigerian suppliers and international buyers.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#C8A951] to-[#B8994A] rounded-full flex items-center justify-center mb-4">
                  <Globe className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#333333] mb-4">Our Vision</h3>
                <p className="text-gray-700">
                  To become a leading partner for African food supply and distribution, recognized globally for sourcing capability, reliable supplier networks, and commitment to connecting Nigerian products with international markets.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide our operations and relationships
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="border-2 border-gray-200 hover:border-[#C8A951] transition-all hover:shadow-lg bg-white">
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#1B5E20] to-[#2E7D32] rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-semibold text-[#333333] mb-3">{value.title}</h3>
                    <p className="text-gray-600 text-sm">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 bg-[#F9F9F9]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#333333] mb-12 text-center">What Sets Us Apart</h2>
            
            <div className="space-y-6">
              {[
                {
                  title: 'Structured Supply Chain Management',
                  description: 'From farm sourcing to final delivery, every step is monitored and optimized for efficiency and quality.'
                },
                {
                  title: 'Temperature-Controlled Logistics',
                  description: 'Reefer container capabilities ensure perishable products maintain freshness throughout international shipping.'
                },
                {
                  title: 'Full Export Compliance',
                  description: 'We handle all phytosanitary certificates, documentation, and regulatory requirements for seamless customs clearance.'
                },
                {
                  title: 'Trusted Shipping Partnerships',
                  description: 'Working with reputable global shipping lines to ensure reliable and timely deliveries.'
                },
                {
                  title: 'Quality Inspection Protocols',
                  description: 'Multi-stage quality checks from sourcing through packing to ensure only the best products reach our clients.'
                },
                {
                  title: 'Dual-Location Operations',
                  description: 'Operating bases in both Nigeria and the UK enable us to serve our clients more effectively.'
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-4 bg-white p-6 rounded-lg shadow-md">
                  <CheckCircle className="text-[#1B5E20] flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="text-xl font-semibold text-[#333333] mb-2">{item.title}</h3>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#333333] mb-4">Our Locations</h2>
            <p className="text-lg text-gray-600">Operating across two continents to serve you better</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-0 shadow-lg bg-gradient-to-br from-[#F9F9F9] to-white">
              <CardContent className="p-8">
                <div className="text-4xl mb-4">🇳🇬</div>
                <h3 className="text-2xl font-bold text-[#333333] mb-2">Nigeria Office</h3>
                <p className="text-gray-700 mb-4">
                  <strong>Location:</strong> Ogun State, Ota<br />
                  <strong>Operations:</strong> Agricultural production, sourcing, quality control, and export processing
                </p>
                <p className="text-sm text-gray-600">
                  Our Nigerian base is at the heart of our agricultural operations, where we work directly with farmers and coordinate all export activities.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-gradient-to-br from-[#F9F9F9] to-white">
              <CardContent className="p-8">
                <div className="text-4xl mb-4">🇬🇧</div>
                <h3 className="text-2xl font-bold text-[#333333] mb-2">United Kingdom Office</h3>
                <p className="text-gray-700 mb-4">
                  <strong>Location:</strong> United Kingdom<br />
                  <strong>Operations:</strong> Client relations, market development, and distribution coordination
                </p>
                <p className="text-sm text-gray-600">
                  Our UK presence allows us to better serve our European clients and understand market demands firsthand.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
